/**
 * Bütçe — Şantiye Karşılama (Landing) Kartları (Faz 34.55+)
 * Tüm bütçe alt-sayfalarında (Gelir/Gider Kayıtları, Kâr-Zarar, Nakit Akış)
 * ortak kullanılan CBS-tarzı şantiye seçim kartları.
 *
 * Fetches /api/budget/site-summary (gelir/gider totals per site) and displays
 * cards in the same visual language as Maliyet Kırınım Yapısı (CBS) landing.
 *
 * Props:
 *   - accent: "emerald" | "rose" | "indigo" | "amber"  (border/hover renk teması)
 *   - title: Sayfa başlığı (ör. "Kâr / Zarar (Ana İcmal)")
 *   - subtitle: Şantiye seçmesi için açıklama
 *   - icon: Header ikonu (Phosphor React element)
 *   - onSelectSite: (siteId) => void
 *   - testIdPrefix: "pl" | "cf" | "income" | "expense"
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  MagnifyingGlass, MapPin, CaretRight, Buildings, TrendUp, TrendDown,
} from "@phosphor-icons/react";

const fmtTL = (n) => new Intl.NumberFormat("tr-TR", {
  style: "currency", currency: "TRY", maximumFractionDigits: 0,
}).format(Number(n) || 0);

const ACCENT_MAP = {
  emerald: {
    bg: "bg-emerald-500/15", icon: "text-emerald-600",
    border: "border-l-emerald-500/40", hoverBorder: "hover:border-l-emerald-600 hover:border-emerald-500/60",
    code: "text-emerald-700", chip: "bg-emerald-50 group-hover:bg-emerald-600",
    cta: "group-hover:text-emerald-700",
  },
  rose: {
    bg: "bg-rose-500/15", icon: "text-rose-600",
    border: "border-l-rose-500/40", hoverBorder: "hover:border-l-rose-600 hover:border-rose-500/60",
    code: "text-rose-700", chip: "bg-rose-50 group-hover:bg-rose-600",
    cta: "group-hover:text-rose-700",
  },
  indigo: {
    bg: "bg-[#1B4776]/10", icon: "text-[#1B4776]",
    border: "border-l-[#1B4776]/40", hoverBorder: "hover:border-l-[#1B4776] hover:border-[#1B4776]/40",
    code: "text-[#1B4776]", chip: "bg-slate-50 group-hover:bg-[#1B4776]",
    cta: "group-hover:text-[#1B4776]",
  },
  amber: {
    bg: "bg-amber-500/15", icon: "text-amber-600",
    border: "border-l-amber-500/40", hoverBorder: "hover:border-l-amber-600 hover:border-amber-500/60",
    code: "text-amber-700", chip: "bg-amber-50 group-hover:bg-amber-600",
    cta: "group-hover:text-amber-700",
  },
};

export default function BudgetSitesLanding({
  title, subtitle, icon: Icon, accent = "indigo",
  onSelectSite, testIdPrefix = "budget",
}) {
  const [summary, setSummary] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("aktif");
  const theme = ACCENT_MAP[accent] || ACCENT_MAP.indigo;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/budget/site-summary`,
          { params: { include_kapali: true } },
        );
        setSummary(data || []);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, []);

  const filtered = useMemo(() => {
    let out = summary || [];
    if (statusFilter !== "all") out = out.filter((s) => s.status === statusFilter);
    if (q) {
      const nq = q.toLowerCase();
      out = out.filter((s) =>
        (s.code || "").toLowerCase().includes(nq) ||
        (s.name || "").toLowerCase().includes(nq) ||
        (s.city || "").toLowerCase().includes(nq),
      );
    }
    return out;
  }, [summary, statusFilter, q]);

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-sites-landing`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-md ${theme.bg} flex items-center justify-center`}>
            {Icon ? <Icon size={22} weight="duotone" className={theme.icon} /> : null}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1B4776]">{title}</h1>
            <p className="text-sm text-slate-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 rounded-none sm:rounded-sm"
                            data-testid={`${testIdPrefix}-sites-landing-status`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Aktif + Kapalı</SelectItem>
              <SelectItem value="aktif">Sadece Aktif</SelectItem>
              <SelectItem value="kapali">Sadece Kapalı</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Şantiye ara..." className="pl-9 w-64"
              data-testid={`${testIdPrefix}-sites-landing-search`} />
          </div>
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card className="p-10 text-center text-slate-400 rounded-none sm:rounded-sm border-slate-200">
          <Buildings size={40} weight="duotone" className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm">
            {q ? "Kriterlere uyan şantiye bulunamadı." : "Henüz aktif şantiye bulunmuyor."}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((s) => {
            const isKapali = s.status === "kapali";
            const net = (Number(s.gelir_total) || 0) - (Number(s.gider_total) || 0);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelectSite && onSelectSite(s.id)}
                data-testid={`${testIdPrefix}-sites-landing-card-${s.code || s.id}`}
                className={`group text-left ${isKapali ? "opacity-70 hover:opacity-100" : ""}`}
              >
                <Card className={`p-5 hover:shadow-lg transition-all cursor-pointer border-l-4 ${
                  isKapali
                    ? "border-l-slate-300/60 hover:border-l-slate-400 hover:border-slate-300"
                    : `${theme.border} ${theme.hoverBorder}`
                }`}>
                  {/* Üst kısım */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-mono font-bold tracking-wider uppercase ${
                          isKapali ? "text-slate-500" : theme.code
                        }`}>
                          {s.code}
                        </span>
                        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                          isKapali
                            ? "bg-slate-100 text-slate-600 border-slate-300"
                            : "bg-emerald-50 text-emerald-700 border-emerald-300"
                        }`}>
                          {isKapali ? "Kapalı" : "Aktif"}
                        </span>
                      </div>
                      <div className="mt-1 text-base font-bold text-[#0F172A] group-hover:text-[#1B4776] transition-colors line-clamp-2">
                        {s.name}
                      </div>
                      {s.city && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                          <MapPin size={13} weight="fill" className="text-[#1B4776]/60" />
                          <span>{s.city}</span>
                          {s.project_manager && (
                            <span className="text-slate-400">· {s.project_manager}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className={`p-2 rounded-full transition-colors ${
                      isKapali
                        ? "bg-slate-100 group-hover:bg-slate-300"
                        : theme.chip
                    }`}>
                      <CaretRight size={16} weight="bold"
                        className="text-slate-400 group-hover:text-white transition-colors" />
                    </div>
                  </div>

                  {/* Alt kısım — Gelir / Gider / Net */}
                  <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <div className="inline-flex items-center gap-1.5">
                        <TrendUp size={12} weight="bold" className="text-emerald-600" />
                        <span className="text-slate-600 font-semibold">Gelir</span>
                        <span className="text-slate-400">({s.gelir_count || 0})</span>
                      </div>
                      <div className="font-mono font-bold text-emerald-700">
                        {fmtTL(s.gelir_total)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <div className="inline-flex items-center gap-1.5">
                        <TrendDown size={12} weight="bold" className="text-rose-600" />
                        <span className="text-slate-600 font-semibold">Gider</span>
                        <span className="text-slate-400">({s.gider_count || 0})</span>
                      </div>
                      <div className="font-mono font-bold text-rose-700">
                        {fmtTL(s.gider_total)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Net</span>
                      <span className={`font-mono text-sm font-bold ${
                        net >= 0 ? "text-[#1B4776]" : "text-rose-700"
                      }`}>
                        {fmtTL(net)}
                      </span>
                    </div>
                  </div>
                </Card>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
